import { useQuery, useMutation } from "@tanstack/react-query"
import {
  getAllRewards,
  getVendorRewards,
  getPendingRewards,
  approvePendingRewards,
  getAvailablePromos,
  getPromoReasons,
  resendReward,
  redeemReward,
} from "@apis/rewards/rewards"
import { rewardsKeys } from "@apis/rewards/queryKeys"

import type { IAccountPromoReason, IPromo } from "./types"
import type { IValueLabel } from "@apis/shared/types"

import {
  transformPromoReasons,
  transformAvailablePromosData,
} from "@apis/rewards/dataTransformations"

export const useGetAllRewardsQuery = () => {
  return useQuery({
    queryKey: rewardsKeys.all,
    queryFn: getAllRewards,
  })
}

export const useGetVendorRewardsQuery = (vendorFulfillmentCode: string) => {
  return useQuery({
    queryKey: rewardsKeys.vendor(vendorFulfillmentCode),
    queryFn: () => getVendorRewards(vendorFulfillmentCode),
  })
}

export const useGetPendingRewardsQuery = (
  fulfillmentVendorCode: string,
  fulfillmentChannel: string,
) => {
  return useQuery({
    queryKey: rewardsKeys.fulfillmentChannel(
      fulfillmentVendorCode,
      fulfillmentChannel,
    ),
    queryFn: () => getPendingRewards(fulfillmentVendorCode, fulfillmentChannel),
  })
}

export const useApprovePendingRewardsMutation = () => {
  return useMutation({
    mutationFn: approvePendingRewards,
  })
}

export const useGetAvailablePromosQuery = <TSelected = IPromo>(
  select?: (data: IPromo[]) => TSelected,
) => {
  return useQuery({
    queryKey: rewardsKeys.availablePromos(),
    queryFn: () => getAvailablePromos(),
    select,
  })
}

export const useGetAvailablePromosFilteredQuery = (measure?: string) =>
  useGetAvailablePromosQuery<IValueLabel[]>((data) =>
    transformAvailablePromosData(data, measure),
  )

export const useGetPromoReasonsQuery = <TSelected = IAccountPromoReason>(
  select?: (data: IAccountPromoReason[]) => TSelected,
) => {
  return useQuery({
    queryKey: rewardsKeys.promoReasons(),
    queryFn: () => getPromoReasons(),
    select,
  })
}

export const useGetPromoReasonsSerializedQuery = () => {
  return useGetPromoReasonsQuery(transformPromoReasons)
}

export const useResendRewardMutation = () => {
  return useMutation({
    mutationFn: resendReward,
  })
}

export const useRedeemRewardMutation = () => {
  return useMutation({
    mutationFn: redeemReward,
  })
}
